/**
 * uscitaTablet.js — non si esce dall'accettazione per sbaglio.
 *
 * PERCHE' ESISTE
 * Il 23 settembre 2026 due pazienti anziani sono usciti a meta'
 * compilazione e hanno perso tutto. `utils/uscitaProtetta.js` teneva il
 * tablet FUORI dalle sue protezioni, e la ragione scritta li' e'
 * giusta: «un dialogo "vuoi uscire senza salvare" davanti a un paziente
 * e' un dialogo che il paziente non sa interpretare». Ma la conclusione
 * era sbagliata. La risposta non e' il dialogo: e' **non perdere i
 * dati**, e dove un dialogo serve lo si scrive in modo che una persona
 * di ottant'anni lo capisca - due pulsanti, nessuna parola tecnica,
 * nessun «senza salvare».
 *
 * I GESTI CHE FACEVANO USCIRE, censiti nel codice il 23 settembre:
 *   1. «Annulla» in fondo alla procedura;
 *   2. «✕» in alto a destra;
 *   3. indietro del browser o gesto di sistema Android;
 *   4. trascinamento verso il basso che ricarica la pagina;
 *   5. chiusura o ricaricamento della scheda;
 *   6. la sessione che sparisce (la segreteria la annulla, o manda il
 *      tablet a un altro paziente);
 *   7. un documento nuovo sullo stesso tablet.
 * Collegamenti verso l'esterno: nessuno, verificato.
 *
 * Questo file chiude 3, 4 e 5, e da' a 1 e 2 una domanda. I due che
 * restano (6 e 7) non sono gesti del paziente e non si chiudono con una
 * domanda: li copre il salvataggio progressivo, perche' quello che il
 * paziente ha gia' scritto e' sul server.
 *
 * IL TRASCINAMENTO si ferma con `overscroll-behavior`, che e' una regola
 * di stile - ma NON sta in un foglio CSS. Il CSS di questa applicazione
 * e' globale: scriverla in `Tablet.css` la applicherebbe a tutto il
 * gestionale, comprese le postazioni della segreteria, che non c'entrano.
 * Si mette e si toglie da qui, cosi' vale per il tempo in cui il tablet
 * sta compilando e per nient'altro.
 */
import { useEffect, useRef } from 'react';

export const DOMANDA_USCITA =
    'Vuoi davvero chiudere? Quello che hai scritto finora resta in '
    + 'memoria e la segreteria può farti riprendere da dove eri.';

/**
 * @param {object} opzioni
 *  - attivo      se false il gancio non fa niente (schermata da PC)
 *  - inCorso     c'e' qualcosa da perdere? (almeno un campo scritto)
 *  - puoTornare  si puo' tornare al passaggio precedente?
 *  - indietro    torna al passaggio precedente
 *  - esci        chiude davvero (gia' confermato)
 */
export function useUscitaTablet({ attivo, inCorso, puoTornare, indietro, esci }) {
    /* I gestori si registrano UNA volta sola: riagganciarli a ogni
       passaggio rifarebbe la voce nella cronologia e il tasto indietro
       comincerebbe a contare i passaggi invece di seguirli. Quello che
       cambia fra un passaggio e l'altro passa da un riferimento. */
    const stato = useRef({ inCorso, puoTornare, indietro, esci });
    stato.current = { inCorso, puoTornare, indietro, esci };

    /* --- indietro del browser e del sistema --- */
    useEffect(() => {
        if (!attivo) return undefined;
        /* Una voce in piu' nella cronologia: il primo «indietro» torna
           qui e non porta via dalla pagina. Si rimette a ogni colpo,
           altrimenti il secondo indietro esce. */
        window.history.pushState({ accettazione: true }, '');
        const alColpo = () => {
            window.history.pushState({ accettazione: true }, '');
            const s = stato.current;
            if (s.puoTornare) s.indietro();
            else if (!s.inCorso) s.esci();
            /* Al primo passaggio con qualcosa scritto non succede
               niente: il tasto indietro non e' il modo di annullare
               un'accettazione, e per farlo c'e' il pulsante, che chiede. */
        };
        window.addEventListener('popstate', alColpo);
        return () => window.removeEventListener('popstate', alColpo);
    }, [attivo]);

    /* --- trascinamento verso il basso che ricarica --- */
    useEffect(() => {
        if (!attivo) return undefined;
        const radice = document.documentElement;
        const prima = radice.style.overscrollBehaviorY;
        /* `contain` e non `none`: dentro la pagina lo scorrimento resta,
           si blocca solo l'effetto che «sfonda» ai bordi e ricarica. */
        radice.style.overscrollBehaviorY = 'contain';
        return () => { radice.style.overscrollBehaviorY = prima; };
    }, [attivo]);

    /* --- chiusura e ricaricamento della scheda --- */
    useEffect(() => {
        if (!attivo) return undefined;
        const allUscita = (e) => {
            if (!stato.current.inCorso) return undefined;
            /* Il testo non si sceglie: i browser mostrano il loro. Serve
               solo dire che c'e' qualcosa in ballo. */
            e.preventDefault();
            e.returnValue = '';
            return '';
        };
        window.addEventListener('beforeunload', allUscita);
        return () => window.removeEventListener('beforeunload', allUscita);
    }, [attivo]);
}

export default useUscitaTablet;
